/** Team reads — `profiles` (the DATABASE user registry that locks roles;
 * v1's Settings AUTH list was the legacy frontend mirror of this).
 * Org-readable via RLS with the session JWT — verified live 2026-07-17.
 * ADMIN WRITES (parity item #3, 2026-07-23): invite / role change / soft
 * remove go through the backend's /api/users routes — org + admin-ness derive
 * from the caller's token server-side; the last active admin is protected. */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import { backendFetch } from "../../lib/backend";

export interface Member {
  id: string | null;
  orgId: string | null;
  name: string | null;
  email: string;
  role: string;
  status: string;
}

/** v1 invite-modal role vocabulary (auth-users.js), verbatim. */
export const ROLE_LABELS: Record<string, { label: string; desc: string }> = {
  admin: { label: "Admin", desc: "full access" },
  manager: { label: "Manager", desc: "full access, no user/integration admin" },
  acquisitions: { label: "Acquisitions", desc: "assigned leads only" },
  tc: { label: "Transaction Coordinator", desc: "assigned deals only" },
  dispo: { label: "Dispo / Sales", desc: "wholesale & buyers" },
  bookkeeper: { label: "Bookkeeper", desc: "financials, read-only" },
  va: { label: "Assistant / VA", desc: "assigned tasks only" },
  member: { label: "Member", desc: "legacy (frontend-only limits)" },
};

const ROLE_ORDER = ["admin", "manager", "acquisitions", "tc", "dispo", "bookkeeper", "va", "member"];

const CACHE = "team_v1";
const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

export function narrowMember(row: Record<string, unknown>): Member | null {
  const email = s(row["email"]);
  if (!email) return null;
  return {
    id: s(row["id"]),
    orgId: s(row["org_id"]),
    name: s(row["full_name"]),
    email,
    role: s(row["role"]) ?? "member",
    status: s(row["status"]) ?? "active",
  };
}

/** admins first, then v1's role order, then by display name. */
export function sortMembers(rows: Member[]): Member[] {
  const ord = (r: string) => {
    const i = ROLE_ORDER.indexOf(r);
    return i >= 0 ? i : ROLE_ORDER.length;
  };
  return [...rows].sort((a, b) => ord(a.role) - ord(b.role) || (a.name ?? a.email).localeCompare(b.name ?? b.email));
}

export async function cachedMembers(): Promise<Member[] | null> {
  const c = await idbGet<{ at: number; rows: Member[] }>(CACHE);
  return c && Array.isArray(c.rows) ? c.rows : null;
}

export async function fetchMembers(token: string, signal?: AbortSignal): Promise<Member[]> {
  const rows = await http<unknown[]>(
    `${SUPABASE_URL}/rest/v1/profiles?select=id,org_id,full_name,email,role,status&limit=200`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, signal, timeoutMs: 15000 },
  );
  const out: Member[] = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const m = narrowMember(r as Record<string, unknown>);
    if (m) out.push(m);
  }
  const sorted = sortMembers(out);
  await idbSet(CACHE, { at: Date.now(), rows: sorted });
  return sorted;
}

// ---- admin writes (backend /api/users, parity item #3) ----------------------

/** Invite by email — Supabase sends the login link; the backend pre-writes the
 * org/role profile so they land in the right org on first sign-in. Returns the
 * new user's id so the caller can pre-seed PERMS restrictions. */
export async function inviteMember(token: string | null, input: { email: string; name?: string; role: string }): Promise<string> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/users/invite", {
    method: "POST",
    timeoutMs: 45000,
    body: { email: input.email, name: input.name ?? "", role: input.role },
  });
  return typeof j?.["id"] === "string" ? (j["id"] as string) : "";
}

export async function setMemberRole(token: string | null, userId: string, role: string): Promise<void> {
  await backendFetch(token, "/api/users/role", {
    method: "POST",
    timeoutMs: 30000,
    body: { user_id: userId, role },
  });
}

/** Soft remove — profiles.status='removed'; the backend refuses their token
 * everywhere from that moment (and refuses to remove the last admin). */
export async function removeMember(token: string | null, userId: string): Promise<void> {
  await backendFetch(token, "/api/users/remove", {
    method: "POST",
    timeoutMs: 30000,
    body: { user_id: userId },
  });
}

/** Self-service display-name change (profiles.full_name via the backend). */
export async function updateMyName(token: string | null, name: string): Promise<void> {
  await backendFetch(token, "/api/users/me", {
    method: "POST",
    timeoutMs: 30000,
    body: { name },
  });
}

/** Self-service password change — straight to Supabase auth with the caller's
 * own session token (no backend, no secrets; auth rejects weak passwords). */
export async function changeMyPassword(token: string, newPassword: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: "PUT",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password: newPassword }),
  });
  if (!res.ok) {
    let detail = String(res.status);
    try {
      const j = (await res.json()) as { msg?: string; message?: string; error_description?: string };
      detail = j.msg ?? j.message ?? j.error_description ?? detail;
    } catch { /* keep status */ }
    throw new Error(detail.slice(0, 140));
  }
}
